// team-card-inertia
import { onDestroy } from "@/modules/_";
import gsap from "@lib/gsap";

// Keep IMG_CLAMP_XY/ROT small enough that drift stays inside the overflow buffer
// between IMG_HOVER_SCALE and VISUAL_SCALE, or img-wrap edges reveal the background.
const IMG_XY_MULT = 15;
const IMG_ROT_MULT = 15;
const IMG_RESISTANCE = 200;
const IMG_CLAMP_XY = 30;
const IMG_CLAMP_ROT = 6;

const CARD_XY_MULT = 8;
const CARD_ROT_MULT = 5;
const CARD_RESISTANCE = 400;
const CARD_CLAMP_XY = 100;
const CARD_CLAMP_ROT = 10;

const VISUAL_SCALE = 0.95;
const IMG_REST_SCALE = 1;
const IMG_HOVER_SCALE = 1.15;
const SCALE_DURATION = 1.2;
const RESET_DURATION = 1;

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  if (
    typeof window !== "undefined" &&
    !window.matchMedia("(hover: hover) and (pointer: fine)").matches
  ) {
    return;
  }

  const cards = Array.from(
    element.querySelectorAll<HTMLElement>('[team-card-inertia="cms-item"]'),
  );
  if (cards.length === 0) return;

  const clampImgXY = gsap.utils.clamp(-IMG_CLAMP_XY, IMG_CLAMP_XY);
  const clampImgRot = gsap.utils.clamp(-IMG_CLAMP_ROT, IMG_CLAMP_ROT);
  const clampCardXY = gsap.utils.clamp(-CARD_CLAMP_XY, CARD_CLAMP_XY);
  const clampCardRot = gsap.utils.clamp(-CARD_CLAMP_ROT, CARD_CLAMP_ROT);

  let prevX = 0;
  let prevY = 0;
  let velX = 0;
  let velY = 0;
  let rafId: number | null = null;

  function onMove(e: MouseEvent) {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      velX = e.clientX - prevX;
      velY = e.clientY - prevY;
      prevX = e.clientX;
      prevY = e.clientY;
      rafId = null;
    });
  }

  element.addEventListener("mousemove", onMove);

  type Entry = {
    card: HTMLElement;
    visual: HTMLElement;
    imgWrap: HTMLElement;
    onEnter: (e: MouseEvent) => void;
    onLeave: () => void;
  };
  const entries: Entry[] = [];

  cards.forEach((card) => {
    const visual = card.querySelector<HTMLElement>(
      '[team-card-inertia="visual-card"]',
    );
    if (!visual) return;
    const imgWrap = visual.querySelector<HTMLElement>(
      '[team-card-inertia="img-wrap"]',
    );
    if (!imgWrap) return;

    gsap.set(visual, { scale: 1 });
    gsap.set(imgWrap, { scale: IMG_REST_SCALE, x: 0, y: 0, rotation: 0 });

    const onEnter = (e: MouseEvent) => {
      // overwrite:"auto" prevents stacked conflicting tweens on rapid enter→leave→enter.
      gsap.to(visual, {
        scale: VISUAL_SCALE,
        duration: SCALE_DURATION,
        ease: "expo.out",
        overwrite: "auto",
      });
      gsap.to(imgWrap, {
        scale: IMG_HOVER_SCALE,
        duration: SCALE_DURATION,
        ease: "expo.out",
        overwrite: "auto",
      });

      // Signed cross product gives torque; dividing by lever distance keeps
      // rotation proportional to pointer speed, not distance from center.
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const ox = e.clientX - cx;
      const oy = e.clientY - cy;
      const rawTorque = ox * velY - oy * velX;
      const leverDist = Math.hypot(ox, oy) || 1;
      const angularForce = rawTorque / leverDist;

      gsap.to(card, {
        inertia: {
          x: { velocity: clampCardXY(velX * CARD_XY_MULT), end: 0 },
          y: { velocity: clampCardXY(velY * CARD_XY_MULT), end: 0 },
          rotation: {
            velocity: clampCardRot(angularForce * CARD_ROT_MULT),
            end: 0,
          },
          resistance: CARD_RESISTANCE,
        },
        overwrite: "auto",
      });

      gsap.to(imgWrap, {
        inertia: {
          x: { velocity: clampImgXY(velX * IMG_XY_MULT), end: 0 },
          y: { velocity: clampImgXY(velY * IMG_XY_MULT), end: 0 },
          rotation: {
            velocity: clampImgRot(angularForce * IMG_ROT_MULT),
            end: 0,
          },
          resistance: IMG_RESISTANCE,
        },
        overwrite: "auto",
      });
    };

    const onLeave = () => {
      gsap.to(visual, {
        scale: 1,
        duration: SCALE_DURATION,
        ease: "expo.out",
        overwrite: "auto",
      });
      gsap.to(imgWrap, {
        scale: IMG_REST_SCALE,
        x: 0,
        y: 0,
        rotation: 0,
        duration: RESET_DURATION,
        ease: "expo.out",
        overwrite: "auto",
      });
      gsap.to(card, {
        x: 0,
        y: 0,
        rotation: 0,
        duration: RESET_DURATION,
        ease: "expo.out",
        overwrite: "auto",
      });
    };

    card.addEventListener("mouseenter", onEnter);
    card.addEventListener("mouseleave", onLeave);
    entries.push({ card, visual, imgWrap, onEnter, onLeave });
  });

  onDestroy(() => {
    element.removeEventListener("mousemove", onMove);
    if (rafId !== null) cancelAnimationFrame(rafId);

    entries.forEach(({ card, visual, imgWrap, onEnter, onLeave }) => {
      card.removeEventListener("mouseenter", onEnter);
      card.removeEventListener("mouseleave", onLeave);
      gsap.killTweensOf(card);
      gsap.killTweensOf(visual);
      gsap.killTweensOf(imgWrap);
      gsap.set(card, { clearProps: "transform,x,y,rotation" });
      gsap.set(visual, { clearProps: "transform,scale" });
      gsap.set(imgWrap, {
        clearProps: "transform,scale,x,y,rotation",
      });
    });
    entries.length = 0;
  });
}
